import { useEffect, useRef, useState } from "react";
import { Crown, Plus } from "lucide-react";
import PlayerJersey from "./PlayerJersey";

// Vertical bands per position row (% from top). GK defends the bottom goal.
// Widened gaps so the credits/points pill of one row never overlaps the row below.
const BANDS = { GK: 88, DEF: 65, MID: 41, FWD: 14 };
const ROW_ORDER = ["FWD", "MID", "DEF", "GK"];

function useCountUp(target, run, duration = 1100) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (!run) { setVal(0); return; }
    const start = performance.now();
    const ease = (p) => 1 - Math.pow(1 - p, 3);
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(target * ease(p));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, run, duration]);
  return val;
}

const CaptainBadge = ({ slot }) => {
  if (slot.is_captain) {
    return (
      <span
        className="cap-badge-pulse absolute -top-1 -right-1 z-20 grid h-5 w-5 place-items-center rounded-full text-[10px] font-extrabold text-slate-900 wc-bg-gold ring-2 ring-slate-900"
        data-testid="captain-badge"
      >C</span>
    );
  }
  if (slot.is_vice_captain) {
    return (
      <span
        className="absolute -top-1 -right-1 z-20 grid h-5 w-5 place-items-center rounded-full bg-slate-200 text-[8px] font-extrabold text-slate-900 ring-2 ring-slate-900"
        data-testid="vice-captain-badge"
      >VC</span>
    );
  }
  return null;
};

const PointsPill = ({ points, show }) => {
  const animated = useCountUp(points || 0, show);
  if (!show) return null;
  return (
    <span
      className="points-pop mt-0.5 rounded-full px-1.5 py-0 text-[10px] font-extrabold leading-tight text-slate-900 wc-bg-gold shadow"
      data-testid="player-points-pill"
    >
      {Math.round(animated)}
    </span>
  );
};

const Chip = ({ slot, x, band, showPoints, onPress, delayMs }) => (
  <button
    type="button"
    onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onPress && onPress(slot); }}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPress && onPress(slot); } }}
    className="pitch-chip absolute z-10 flex w-[76px] -translate-x-1/2 -translate-y-1/2 flex-col items-center focus:outline-none"
    style={{ left: `${x}%`, top: `${band}%`, touchAction: "manipulation" }}
    data-testid={`pitch-slot-${slot.player_id}`}
  >
    <div className="chip-in relative" style={{ animationDelay: `${delayMs}ms` }}>
      <CaptainBadge slot={slot} />
      <PlayerJersey player={slot} size={48} />
    </div>
    <span className="-mt-1 max-w-[72px] truncate rounded bg-slate-900/80 px-1.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
      {slot.name?.split(" ").slice(-1)[0] || "Player"}
    </span>
    {showPoints ? (
      <PointsPill points={slot.points_earned} show={showPoints} />
    ) : (
      <span className="mt-0.5 text-[10px] font-bold wc-gold">{slot.credits}Cr</span>
    )}
  </button>
);

const EmptyChip = ({ x, band, label, onPress }) => (
  <button
    type="button"
    onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onPress && onPress(); }}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPress && onPress(); } }}
    className="pitch-chip absolute z-10 flex w-[76px] -translate-x-1/2 -translate-y-1/2 flex-col items-center"
    style={{ left: `${x}%`, top: `${band}%`, touchAction: "manipulation" }}
    data-testid={`pitch-empty-${label}`}
  >
    <span className="grid h-12 w-12 place-items-center rounded-full border-2 border-dashed border-white/50 bg-white/10 text-white/80">
      <Plus size={20} />
    </span>
    <span className="mt-1 text-[10px] font-semibold text-white/70">{label}</span>
  </button>
);

const PitchMarkings = () => (
  <svg
    className="absolute inset-0 h-full w-full"
    viewBox="0 0 100 150"
    preserveAspectRatio="none"
    style={{ stroke: "rgba(255,255,255,0.45)", strokeWidth: 0.4, fill: "none" }}
  >
    <rect x="3" y="3" width="94" height="144" rx="1" />
    <line x1="3" y1="75" x2="97" y2="75" />
    <circle cx="50" cy="75" r="11" />
    <circle cx="50" cy="75" r="0.8" style={{ fill: "rgba(255,255,255,0.6)" }} />
    {/* bottom box (GK end) */}
    <rect x="26" y="123" width="48" height="24" />
    <rect x="39" y="141" width="22" height="6" />
    <path d="M39 123 A11 11 0 0 1 61 123" />
    {/* top box */}
    <rect x="26" y="3" width="48" height="24" />
    <rect x="39" y="3" width="22" height="6" />
    <path d="M39 27 A11 11 0 0 0 61 27" />
  </svg>
);

/**
 * PitchView — premium pitch graphic with formation-positioned player chips,
 * captain/VC badges, animated points reveal, and a super-sub bench.
 *
 * Props:
 *   squad: slot[]  (non-super-sub rendered on pitch; is_super_sub on bench)
 *   showPoints: bool — reveal animated points pills
 *   onSlotPress: (slot) => void
 *   emptySlots: {GK,DEF,MID,FWD} optional — render dashed placeholders in build mode
 *   onEmptyPress: (position) => void
 */
export const PitchView = ({ squad = [], showPoints = false, onSlotPress, emptySlots, onEmptyPress }) => {
  const main = squad.filter((s) => !s.is_super_sub);
  const bench = squad.filter((s) => s.is_super_sub);

  const rows = ROW_ORDER.map((pos) => ({ pos, players: main.filter((s) => s.position === pos) }));

  let delay = 0;

  return (
    <div className="football-theme" data-testid="pitch-view">
      <div className="pitch-surface relative aspect-[3/4] w-full overflow-hidden rounded-2xl">
        <PitchMarkings />
        {rows.map(({ pos, players }) => {
          // Empty slots are formation-aware (computeEmptySlots in utils/football.js):
          // picked + empty === 11 across positions. Render exactly emptySlots[pos] here.
          const emptyCount = emptySlots ? (emptySlots[pos] || 0) : 0;
          const total = players.length + emptyCount;
          const band = BANDS[pos];
          return (
            <div key={pos}>
              {players.map((slot, i) => {
                delay += 60;
                return (
                  <Chip
                    key={slot.player_id}
                    slot={slot}
                    x={((i + 1) / (total + 1)) * 100}
                    band={band}
                    showPoints={showPoints}
                    onPress={onSlotPress}
                    delayMs={delay}
                  />
                );
              })}
              {Array.from({ length: emptyCount }).map((_, j) => (
                <EmptyChip
                  key={`${pos}-empty-${j}`}
                  x={((players.length + j + 1) / (total + 1)) * 100}
                  band={band}
                  label={pos}
                  onPress={() => onEmptyPress && onEmptyPress(pos)}
                />
              ))}
            </div>
          );
        })}
      </div>

      {bench.length > 0 && (
        <div className="mt-3 rounded-xl border border-border bg-card/60 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Crown size={13} className="wc-gold" /> Super Subs
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide">
            {bench.map((slot) => (
              <div key={slot.player_id} className="flex w-16 shrink-0 flex-col items-center" data-testid={`bench-slot-${slot.player_id}`}>
                <PlayerJersey player={slot} size={40} />
                <span className="mt-1 max-w-[64px] truncate text-[11px] font-semibold">{slot.name?.split(" ").slice(-1)[0]}</span>
                {showPoints && <PointsPill points={slot.points_earned} show={showPoints} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PitchView;
